import { NgClass } from '@angular/common';
import { Component, Input, OnInit, inject, output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';

import { AnchorDirective, CurrencyPipe, CurrencySymbolPipe, DeviceService, DynamicHtmlDirective, NavigationService } from '@frontend/vanilla/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AssetsData } from '@payments/common-utils';
import { AccountFormatPipe } from '@payments/common-utils';
import * as braintree from 'braintree-web';
import { NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { Subscription } from 'rxjs';

import { AppConstants, ChannelType, Config, Country, CurrencyCode, InputType, ProductType } from '../../../config/appConstants';
import { UrlConstants } from '../../../config/urlConstants';
import { PaymentOption } from '../../../model/common.model';
import { DepositInfo } from '../../../model/depositInfo.model';
import { MessageType } from '../../../model/message.model';
import { QDInstrument } from '../../../model/qdInstrument.model';
import { BehaviorDataService } from '../../../service/behaviorData.service';
import { DataService } from '../../../service/data.service';
import { DepositService } from '../../../service/deposit.service';
import { DepositRedirectService } from '../../../service/depositRedirect.service';
import { EventService } from '../../../service/event.service';
import { GtmService } from '../../../service/gtm.service';
import { LoaderService } from '../../../service/loader.service';
import { OptionListService } from '../../../service/optionList.service';
import { ResizeService } from '../../../service/resize.service';
import { UserDataService } from '../../../service/userDataService';
import { MessageComponent } from '../../../shared/components/messages/message-component';
import { ThresholdLimitMessagesComponent } from '../../../shared/components/threshold-limit-messages/threshold-limit-messages.component';
import { RedirectMessageComponent } from '../additional-messages/redirect-message/redirect-message.component';
import { TransactionCurrencyComponent } from '../additional-messages/transaction-currency/transaction-currency.component';
import { AmountComponent } from '../amount/amount.component';
import { CCInputComponent } from '../cc-input/cc-input.component';
import { PasswordBlockComponent } from '../cc-input/password-block/password-block.component';
import { DepositLimitMessageComponent } from '../deposit-limit-message/deposit-limit-message.component';
import { Flash1VoucherInputComponent } from '../flash1voucher/flash1voucher-input.component';
import { OptionContentComponent } from './option-content/option-content.component';
import { ProductPreferenceComponent } from './product-preference/product-preference.component';

export interface labelConfigData {
    isEnabled: boolean;
    selectedInfo: any;
}
@Component({
    selector: 'app-options-list',
    templateUrl: './options-list.component.html',
    styleUrls: ['./options-list.component.scss'],
    imports: [
        DynamicHtmlDirective,
        ProductPreferenceComponent,
        FormsModule,
        ReactiveFormsModule,
        MatLabel,
        MatFormField,
        MatSelect,
        MatSelectTrigger,
        OptionContentComponent,
        NgxPopperjsModule,
        MatOption,
        MessageComponent,
        RedirectMessageComponent,
        NgClass,
        AmountComponent,
        CCInputComponent,
        Flash1VoucherInputComponent,
        ThresholdLimitMessagesComponent,
        DepositLimitMessageComponent,
        PasswordBlockComponent,
        TransactionCurrencyComponent,
        TranslateModule,
        CurrencyPipe,
        AccountFormatPipe,
        AnchorDirective,
        CurrencySymbolPipe,
    ],
})
export class OptionsListComponent implements OnInit {
    depositService = inject(DepositService);
    private depositRedirectService = inject(DepositRedirectService);
    private dataService = inject(DataService);
    private userDataService = inject(UserDataService);
    private behaviorData = inject(BehaviorDataService);
    loaderService = inject(LoaderService);
    optionListService = inject(OptionListService);
    private gtmService = inject(GtmService);
    private translate = inject(TranslateService);
    private deviceService = inject(DeviceService);
    private resizeService = inject(ResizeService);
    assetsData = inject(AssetsData);
    private eventService = inject(EventService);

    paymentMethod = output<string>();
    private navigationService = inject(NavigationService);
    @Input() instrumentFormGroup: FormGroup<{
        amountFieldFormGroup: FormGroup;
        instrumentFormCtrl: FormControl;
        instrumentInputFormGroup?: FormGroup;
        productPreferenceFormGroup?: FormGroup;
        pixAccountSelect?: FormControl;
        consentForRegistration?: FormControl;
    }>;
    optionsList: QDInstrument[];
    inputType: string;
    ccFamily = AppConstants.CCFAMILY;
    prepaidCards = AppConstants.PREPAIDCARDS;
    initialValue: string;
    isUSLabel: boolean;
    usedOptions: PaymentOption[] = [];
    ccDepositLimitCheckEnabled: boolean;
    ccCurrentDepositLimit: string;
    isInstantEchecks: boolean;
    rgLimitData: any;
    depositInfo: DepositInfo;
    germanAMLChangesEnabled: boolean;
    defaultCurrency: boolean = true;
    selectedPaymentMethod: PaymentOption | undefined;
    subscriptions: Subscription[] = [];
    maxFailDepEnhancementEnabled: boolean = false;
    enablePrepaidcardRestrictions: boolean = false;
    changeMaxFailDepEnhancement$: Subscription = new Subscription();
    isMobile: boolean;
    isESIntensivePlayer: boolean;
    isESRiskPlayer: boolean;
    moreInfoIntensePlayerLink: string;
    liveChatRiskPlayerUrl: string;
    currencyUSD: string = 'USD';
    pixBankRegistration: labelConfigData = {
        isEnabled: false,
        selectedInfo: {},
    };
    userData: any;
    isuserODavailble: boolean;
    OccuDetails: any;
    showBrazilBankList: boolean = false;
    showBrazilConsent: boolean = false;
    public get messageType(): typeof MessageType {
        return MessageType;
    }
    showPreemptiveMsg: boolean = false;
    es_allowed_amount: number;

    constructor() {
        const optionListService = this.optionListService;

        this.optionsList = optionListService.optionList;
        this.depositInfo = this.depositService.depositInfo;
        this.isMobile = this.deviceService.isMobile;
    }

    ngOnInit() {
        this.inputType = this.optionsList[0]?.paymentMethodInfo?.paymentMethod ?? '';
        this.paymentMethod.emit(this.inputType);
        this.initialValue = this.optionsList[0]?.instrumentNumber ?? '';
        this.userData = this.userDataService?.userData;
        this.enablePrepaidcardRestrictions = this.userDataService?.userData?.configParams?.enable_prepaidcard_restrictions == 'Y';
        const lang = this.dataService.sessionInfo?.langId ?? 'en';
        this.liveChatRiskPlayerUrl = `${this.userDataService?.userData?.configParams?.merchant_hostname}/${lang}${UrlConstants.LIVE_CHAT_ES_LINK}`;
        this.moreInfoIntensePlayerLink = `${this.userDataService?.userData?.configParams?.merchant_hostname}/${lang}${UrlConstants.MORE_INFO_ES_LINK}`;
        this.isuserODavailble = this.userDataService?.userData?.configParams?.isPlayerJobDetailsUpdated == 'N';
        if (this.isuserODavailble) {
            this.OccuDetails = this.behaviorData.userODetails;
        }
        if (this.optionsList?.length > 0) {
            this.usedOptions = [];
            this.optionsList.map((option) => {
                const newOption = new PaymentOption();
                const paymentMethod = option?.paymentMethodInfo?.paymentMethod;
                newOption.imgPath = AppConstants.IMAGE_PATH_PAYMENT_OPTIONS + paymentMethod.toLowerCase() + '.svg';
                newOption.ESIntensiveGreyOut = option?.paymentAccountInfo?.ESIntensiveGreyOut == 'true' && option?.creditCard == true;
                newOption.isESRiskPlayer = option?.paymentAccountInfo?.isESRiskPlayer == 'Y';
                newOption.instrumentNumber = option?.instrumentNumber;
                newOption.paymentMethod = paymentMethod.toLowerCase();
                newOption.paymentText = paymentMethod.charAt(0).toUpperCase() + paymentMethod.substring(1).toLowerCase();
                newOption.sportsOnly = option?.sportsOnly;
                newOption.bankAccountsOnly = option?.bankAccountsOnly;
                newOption.BANK_INSTRUMENT_LIST = option.details?.BANK_INSTRUMENT_LIST;
                newOption.userMaxFailDepStatus = option?.details?.userMaxFailDepStatus;
                if (newOption.userMaxFailDepStatus?.expiryTime) {
                    setTimeout(() => {
                        if (newOption.userMaxFailDepStatus) newOption.userMaxFailDepStatus.blocked = false;
                    }, newOption.userMaxFailDepStatus.expiryTime * 1000);
                }
                newOption.es_pre_emptive_threshold_limit = option?.paymentAccountInfo?.es_pre_emptive_threshold_limit;
                newOption.es_deposited_amount = option?.paymentAccountInfo?.es_deposited_amount;
                newOption.es_threshold_limit = option?.paymentAccountInfo?.es_threshold_limit;
                newOption.es_allowed_amount = option?.paymentAccountInfo?.es_allowed_amount;
                newOption.es_jumio_verification_url = option?.paymentAccountInfo?.es_jumio_verification_url;
                this.usedOptions.push(newOption);
            });

            if (this.usedOptions?.length > 0) {
                this.selectedPaymentMethod = this.usedOptions[0];
                this.es_allowed_amount = Number(this.selectedPaymentMethod?.es_allowed_amount);
            }
        }

        this.buildFormGroups();
        if (this.optionsList?.[0]) {
            this.optionListService.selectedOption = this.optionsList[0];
        }
        this.depositService.setCurrencyConversion(this.optionListService?.selectedOption);
        this.isUSLabel = this.userDataService?.userData?.userInfo?.addressInfo?.country === Country.US;
        this.ccDepositLimitCheckEnabled = this.userDataService?.userData?.configParams?.isCreditCardDepositLimitCheckEnabled === Config.YES;
        if (this.ccDepositLimitCheckEnabled) {
            this.ccCurrentDepositLimit = this.userDataService?.userData?.configParams?.currentCreditCardDepositLimit;
        }
        if (this.depositInfo?.isBelgiumRgDecreeEnabled) {
            const rgLimitInterval = setInterval(() => {
                this.rgLimitData = this.dataService?.RgLimitData;
                if (this.rgLimitData[0]?.rgLimitInfo?.length > 0) {
                    clearInterval(rgLimitInterval);
                }
            }, 100);
            setTimeout(() => {
                if (rgLimitInterval && !this.rgLimitData?.rglimitInfo) {
                    clearInterval(rgLimitInterval);
                }
            }, 1000);
        }
        this.germanAMLChangesEnabled = this.userDataService?.userData?.configParams?.germanAMLChangesEnabled === Config.YES;
        this.maxFailDepEnhancementEnabled = this.userDataService?.userData?.configParams.maxFailDepEnhancementEnabled === Config.YES;
        this.setInputTypeDefaults();
        this.changeMaxFailDepEnhancement$ = this.behaviorData.changeMaxFailDepEnhancement.subscribe((res: any) => {
            if (res?.blocked && res?.expiryTime) {
                const mfdePaymentOption = this.usedOptions.find(
                    (option) => option?.instrumentNumber === this.selectedPaymentMethod?.instrumentNumber,
                );
                const mfdeQDinstrument = this.optionsList.find((option) => option?.instrumentNumber === this.selectedPaymentMethod?.instrumentNumber);
                if (mfdeQDinstrument && mfdePaymentOption) {
                    if (!mfdeQDinstrument?.details) {
                        mfdeQDinstrument.details = {};
                    }
                    mfdeQDinstrument.details.userMaxFailDepStatus = res;
                    mfdePaymentOption.userMaxFailDepStatus = res;
                    if (res?.blocked) {
                        setTimeout(() => {
                            res.blocked = false;
                        }, res.expiryTime * 1000);
                    }
                }
                if (!this.usedOptions.find((option) => !option?.userMaxFailDepStatus?.blocked)) {
                    this.optionListService.redirectToMainCashier('&type=options');
                }
            }
        });
        this.subscriptions.push(this.changeMaxFailDepEnhancement$);
        if (this.isESIntensivePlayer) {
            this.gtmService.intensivePlayerMsgLoad(this.inputType);
        }
        this.behaviorData.getEnteredAmount.subscribe((data) => {
            this.showPreemptiveMsg = false;
            if (
                Number(this.selectedPaymentMethod?.es_deposited_amount) + Number(data) >=
                    Number(this.selectedPaymentMethod?.es_pre_emptive_threshold_limit) &&
                Number(this.selectedPaymentMethod?.es_deposited_amount) + Number(data) <= Number(this.selectedPaymentMethod?.es_threshold_limit) &&
                !this.isExpiredIdValidation()
            ) {
                this.showPreemptiveMsg = true;
                this.gtmService.raiseEventForInsVerLoad(this.selectedPaymentMethod?.paymentMethod + ' - under 2k optional banner');
            }
        });
    }

    verifyAccount(option: any) {
        const urlTemp = option?.es_jumio_verification_url?.replace('{paymentMethodName}', option?.paymentMethod) + '&cashierQd=true';
        this.gtmService.raiseEventForInsVerClick(this.selectedPaymentMethod?.paymentMethod + ' - under 2k optional banner', 'verify', urlTemp);
        this.eventService.raiseCloseEvent();
        this.navigationService.goTo(urlTemp, { forceReloadTarget: 'top' });
    }

    get amountFieldFormGroup(): FormGroup {
        return this.instrumentFormGroup?.get('amountFieldFormGroup') as FormGroup;
    }
    buildFormGroups() {
        this.instrumentFormGroup.addControl('instrumentFormCtrl', new FormControl(this.usedOptions[0]));
        this.instrumentFormGroup.addControl('instrumentInputFormGroup', new FormGroup({}));
        this.instrumentFormGroup.addControl('amountFieldFormGroup', new FormGroup({}));
        this.instrumentFormGroup.addControl('productPreferenceFormGroup', new FormGroup({}));
        this.addPixAccount();
    }
    addPixAccount() {
        this.pixBankRegistration.isEnabled = !!(
            this.userData?.configParams?.pixBrInfo?.isConservative && this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.length
        );
        const { activeRegisteredbankAccounts = 0, br_ftd_consent_auth_enabled = '' } = this.userData.configParams || {};
        if (
            br_ftd_consent_auth_enabled == 'Y' &&
            Number(activeRegisteredbankAccounts) > 0 &&
            this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.length
        ) {
            this.showBrazilBankList = true;
        }
        if (this.pixBankRegistration.isEnabled || this.showBrazilBankList) {
            const defaultBank = this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.[0];
            this.pixBankRegistration.selectedInfo = defaultBank;
            const defaultValue = defaultBank ? `${defaultBank.bankName}-${defaultBank.accountNumber}` : '';
            this.pixBankRegistration.selectedInfo = this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.[0];
            const dCtrl = new FormControl('pixAccountSelect');
            this.instrumentFormGroup.addControl('pixAccountSelect', new FormControl(defaultValue));
            dCtrl.setValidators(Validators.required);
            dCtrl.updateValueAndValidity();
            this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.pixBankRegistration.selectedInfo.accountNumber;
        }
        if (this.userData?.configParams?.pixBrInfo?.isNewBrLabel) {
            this.resizeService.resizeCashier();
        }
    }
    removePixControl() {
        this.pixBankRegistration.isEnabled && this.instrumentFormGroup.removeControl('pixAccountSelect');
    }

    onChange(instrumentName: MatSelectChange) {
        this.selectedPaymentMethod = this.usedOptions.find((option) => option?.instrumentNumber === instrumentName?.value?.instrumentNumber);
        this.es_allowed_amount = Number(this.selectedPaymentMethod?.es_allowed_amount);
        if (instrumentName.value === InputType.allDepositOptions) {
            this.selectedPaymentMethod = new PaymentOption();
            this.selectedPaymentMethod.paymentMethod = 'moreoptions';
            this.selectedPaymentMethod.instrumentNumber = this.translate.instant('PARTY.CASHIER.web.deposit.more.options');
            //Track event for more options click
            this.gtmService.raiseEventForMoreOptions();
            if (
                this.dataService?.sessionInfo?.channelId === ChannelType.WC ||
                this.dataService?.sessionInfo?.channelId === ChannelType.VC ||
                this.dataService?.sessionInfo?.channelId === ChannelType.FC ||
                this.dataService?.sessionInfo?.channelId === ChannelType.MC ||
                this.dataService?.sessionInfo?.productId === ProductType.BINGO
            ) {
                this.optionListService.postMessageToParent(this.dataService?.sessionInfo?.productId);
            } else {
                this.optionListService.redirectToMainCashier('&type=options');
            }
            return;
        } else {
            this.instrumentFormGroup.removeControl('instrumentInputFormGroup');
            this.instrumentFormGroup.addControl('instrumentInputFormGroup', new FormGroup({}));
            this.instrumentFormGroup.removeControl('productPreferenceFormGroup');
            this.instrumentFormGroup.addControl('productPreferenceFormGroup', new FormGroup({}));
            this.removePixControl();
            const selectedOption = this.optionsList.find((option) => option?.instrumentNumber === instrumentName?.value?.instrumentNumber);
            if (selectedOption) {
                this.inputType = selectedOption?.paymentMethodInfo?.paymentMethod;
                this.paymentMethod.emit(this.inputType);
                this.optionListService.selectedOption = selectedOption;
                this.setInputTypeDefaults();
                this.depositService.paypalDirectBadFunding = null;
            }
        }
        this.resizeService.resizeCashier();
    }

    setInputTypeDefaults() {
        if (this.optionListService?.selectedOption) {
            // clearing previous transaction details on instrument change when popup blocer is active
            if (this.depositRedirectService.popupBlocked()) {
                this.clearPrevTransData();
            }
            this.showChangeCurrency();
            this.depositService.depositInfo.paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
            this.depositService.depositInfo.amountInfo.nativeCurrency =
                this.optionListService?.selectedOption?.currencyInfo?.defaultCurr ||
                this.optionListService?.selectedOption?.currencyInfo?.currencyList[0];
            this.depositService.depositInfo.paymentAccountInfo.paymentAccountId =
                this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountId;
            this.depositService.setCurrencyConversion(this.optionListService?.selectedOption);
            if (this.ccFamily.includes(this.inputType)) {
                this.depositService.depositInfo.paymentAccountInfo.ccId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
                this.isESIntensivePlayer =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.ESIntensiveGreyOut == 'true' &&
                    this.optionListService?.selectedOption?.creditCard == true;
            }
            if (this.inputType === InputType.PAYPAL) {
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
            }
            if (this.inputType === InputType.PIX) {
                this.addPixAccount();
            }
            if (this.inputType === InputType.CANADIANEFT) {
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
            }
            if (this.inputType === InputType.VENMO) {
                this.depositService.depositInfo.paymentAccountInfo.paymentAccountId =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountId;
                this.depositService.depositInfo.paymentAccountInfo.venmo_firstName = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_lastName = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_email = this.optionListService?.selectedOption?.instrumentNumber;
                this.depositService.depositInfo.paymentAccountInfo.venmo_phone = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_nonce = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_collectedDeviceData = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_payment_data =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountId;
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
            }
            if (this.inputType === InputType.INSTANTECHECKS) {
                this.isInstantEchecks = true;
                this.depositService.depositInfo.paymentAccountInfo.accountId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
                this.depositService.depositInfo.paymentAccountInfo.vipPreferredAccountNumber =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.vipPreferredAccountNumber;
            }
            if (this.prepaidCards.includes(this.inputType)) {
                this.depositService.depositInfo.playPlusCashId = this.optionListService?.selectedOption?.playPlusCashId;
            }

            if (this.inputType === InputType.VENMO) {
                this.getVemoCollectedDeviceData();
            }
            if (this.inputType === InputType.PAYPALDIRECT) {
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
                if (this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeToken) {
                    this.getDeviceDataBrainTree(this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeToken);
                }
            }
            if (
                this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeSDKEnable == 'Y' &&
                this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeAuthKey
            ) {
                this.getDeviceDataBrainTree(this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeAuthKey);
            }
        }
        this.depositService.depositInfo.amountInAccountCurrency = '';
        this.depositService.depositInfo.amountInfo.nativeAmountString = '';
        this.depositService.depositInfo.amountCharged = '';

        if (this.inputType === InputType.FLASH1VOUCHER) {
            this.depositService.depositInfo.amountInfo.nativeCurrency = CurrencyCode.ZAR;
            this.depositService.depositInfo.amountInfo.nativeAmountString = '0';
        }
        this.isESRiskPlayer = this.optionListService?.selectedOption?.paymentAccountInfo?.isESRiskPlayer == 'Y';
    }
    isIntensivePlayer() {
        this.gtmService.intensivePlayerMsgLoad(this.inputType);
    }
    moreInfoLink() {
        this.gtmService.intensivePlayerMsgClickOnMoreInfo(this.moreInfoIntensePlayerLink);
        this.navigationService.goTo(this.moreInfoIntensePlayerLink, { forceReloadTarget: 'top' });
    }
    showChangeCurrency() {
        const defaultCurr = this.optionListService?.selectedOption?.currencyInfo?.defaultCurr;
        const accountCurr = this.userDataService?.userData?.userInfo?.accountCurrency;
        this.depositService.depositInfo.amountInfo.nativeCurrency = defaultCurr;
        this.defaultCurrency = defaultCurr === accountCurr ? true : false;
    }

    clearPrevTransData() {
        this.depositRedirectService.updatePopupStatus(false);
        this.loaderService.depositRedirectInProgress.set(false);
        this.depositRedirectService.resetdepositRedirectInfo();
        this.loaderService.isLoading.next(false);
    }

    getDeviceDataBrainTree(brainTreeToken: any) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        braintree.client
            .create({
                authorization: brainTreeToken,
            })
            .then(function (clientInstance) {
                return braintree.dataCollector.create({
                    client: clientInstance,
                });
            })
            .then(function (dataCollectorInstance: any) {
                if (self.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeSDKEnable == 'Y') {
                    self.depositService.depositInfo.paymentAccountInfo.isBTSDKSuccess = 'Y';
                    self.depositService.depositInfo.paymentAccountInfo.brainTreeDeviceData = dataCollectorInstance?.rawDeviceData
                        ? dataCollectorInstance?.rawDeviceData?.correlation_id
                        : '';
                    self.depositService.depositInfo.paymentAccountInfo.customerBrowser = self.detectBrowser();
                } else {
                    self.depositService.depositInfo.paymentAccountInfo.deviceData = dataCollectorInstance?.rawDeviceData
                        ? dataCollectorInstance?.rawDeviceData?.correlation_id
                        : '';
                }
            })
            .catch(() => {
                self.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeSDKEnable == 'Y'
                    ? (self.depositService.depositInfo.paymentAccountInfo.isBTSDKSuccess = 'N')
                    : '';
            });
    }

    detectBrowser() {
        const userAgent = navigator.userAgent;
        if (userAgent.indexOf('Edg') > -1) {
            return 'Microsoft Edge';
        } else if (userAgent.indexOf('Chrome') > -1) {
            return 'Chrome';
        } else if (userAgent.indexOf('Firefox') > -1) {
            return 'Firefox';
        } else if (userAgent.indexOf('Safari') > -1) {
            return 'Safari';
        } else if (userAgent.indexOf('Opera') > -1) {
            return 'Opera';
        } else if (userAgent.indexOf('Trident') > -1 || userAgent.indexOf('MSIE') > -1) {
            return 'Internet Explorer';
        }

        return 'Unknown';
    }

    getVemoCollectedDeviceData() {
        const authorization = this.optionListService?.selectedOption?.paymentAccountInfo?.VENMO_CLIENT_AUTHORIZATION;
        braintree.client
            .create({
                authorization: authorization,
            })
            .then((clientInstance) => {
                return Promise.all([
                    braintree.dataCollector.create({
                        client: clientInstance,
                        paypal: true,
                    }),
                ]);
            })
            .then((results) => {
                if (results && results[0]) {
                    const dataCollectorInstance: any = results[0];
                    const collectedDeviceData =
                        dataCollectorInstance && dataCollectorInstance?.rawDeviceData && dataCollectorInstance?.rawDeviceData?.correlation_id
                            ? dataCollectorInstance?.rawDeviceData?.correlation_id
                            : null;
                    this.depositService.depositInfo.paymentAccountInfo.venmo_collectedDeviceData = collectedDeviceData;
                }
            });
    }
    onPixAccountChange(selected: any) {
        const index: number =
            this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.findIndex(
                (bank: any) => bank?.bankName + '-' + bank?.accountNumber == selected?.value,
            ) ?? -1;
        if (index != -1 && this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST)
            this.pixBankRegistration.selectedInfo = this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST[index];
        // Track event when user selects any of the bank account:
        if (this.showBrazilBankList && index != -1) {
            this.gtmService.raiseEventForBrazilBankAccountReg('select', 'not applicable', 'click', 'bank account dropdown', '', index + 1);
        }
    }
    onAccountLinkClick() {
        if (this.showBrazilBankList) {
            const consentCtrl = new FormControl(false, Validators.requiredTrue);
            this.instrumentFormGroup?.addControl('consentForRegistration', consentCtrl);
            this.showBrazilBankList = false;
            this.showBrazilConsent = true;
            // Track event when clicks on deposit from new account:
            this.gtmService.raiseEventForBrazilBankAccountReg('deposit from new account', 'not applicable', 'click', 'bank account dropdown');
            this.gtmService.raiseEventForBrazilBankAccountReg('bank consent checkbox', 'not applicable', 'load', 'bank consent checkbox');
            return;
        }
        const { activeRegisteredbankAccounts, bank_registration_allowed_count } = this.userDataService.userData.configParams;
        let params = '&type=';
        if (activeRegisteredbankAccounts < bank_registration_allowed_count) {
            //&& moduleType == 'bank-account-register'
            params += 'bank-account-register/create';
        } else {
            params += 'manage-bank-accounts';
        }
        const btnName =
            this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.length &&
            this.selectedPaymentMethod?.BANK_INSTRUMENT_LIST?.length >= this.userData?.configParams?.bank_registration_allowed_count
                ? 'manage accounts'
                : 'register new account';
        //TODO check and optimize the url clicked value here
        this.gtmService.raiseEventForBrazilBankAccountReg(
            btnName,
            this.dataService.sessionInfo?.langId + 'payments' + params,
            'click',
            'pix bank dropdown',
        );
        this.optionListService.redirectToMainCashier(params);
    }
    isExpiredIdValidation() {
        return (
            this.userDataService?.userData?.configParams?.isSpainExpiryKycRefreshCheckEnabled == 'Y' &&
            this.userDataService?.userData?.configParams?.rekycSubErrorCode == '604' &&
            Number(this.selectedPaymentMethod?.es_allowed_amount) > Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit)
        );
    }
    get consentFormControl(): FormControl | null {
        return this.instrumentFormGroup?.get('consentForRegistration') as FormControl | null;
    }
    onConsentChange(event: Event) {
        // Track event when user clicks on checkbox
        const checked = (event.target as HTMLInputElement).checked;
        if (this.showBrazilConsent) {
            this.gtmService.raiseEventForBrazilBankAccountReg(checked ? 'check' : 'uncheck', 'not applicable', 'click', 'bank consent checkbox');
        }
    }
    onDropdownOpened() {
        // Track event when clicks on select bank account , track open event
        this.gtmService.raiseEventForBrazilBankAccountReg('open', 'not applicable', 'click', 'bank account dropdown');
    }

    protected readonly NgxPopperjsPlacements = NgxPopperjsPlacements;
    protected readonly NgxPopperjsTriggers = NgxPopperjsTriggers;
}
